<template>
  <div class="dash-page">

    <!-- Full-bleed background (mirrors login page) -->
    <div class="dash-bg" aria-hidden="true">
      <div class="dash-bg__overlay"></div>
    </div>

    <AcceptableUse v-if="!accepted" :currentVersion="eulaVersion"/>

    <!-- No tenants warning -->
    <div v-if="noTenants" class="dash-warn">
      <ExclamationIcon class="dash-warn__icon" aria-hidden="true" />
      <div>
        <h3 class="dash-warn__title">ATTENTION</h3>
        <p class="dash-warn__body">You have not been assigned an organization.</p>
      </div>
    </div>

    <!-- Single-tenant redirect notice -->
    <RedirectingOverlay v-if="redirecting" :tenantName="selectedTenant.tenant_name" />

    <!-- Content panel -->
    <div v-if="!redirecting" class="dash-card">
      <div class="dash-card__header">
        <h1 class="dash-card__title">Your Organizations</h1>
        <p class="dash-card__sub">Select an organization to continue.</p>
      </div>

      <!-- Search -->
      <div class="dash-search-wrap">
        <div class="dash-search">
          <SearchIcon class="dash-search__icon" aria-hidden="true" />
          <input
            type="text"
            name="searchTenant"
            id="searchTenant"
            v-model="searchTerm"
            class="dash-search__input"
            placeholder="Search organizations…"
          />
        </div>
      </div>

      <!-- Grid -->
      <ul role="list" class="dash-grid">
        <li v-for="item in filteredList" :key="item.id" class="dash-grid__item">
          <TenantCard :source=item @select="handleTenantSelect" />
        </li>
      </ul>
    </div>
  </div>
</template>

<script>

import {mapGetters, mapMutations} from "vuex";
import { ExclamationIcon, SearchIcon } from '@heroicons/vue/solid'
import {getEmployeesByUserId} from "@/services/user.service";
import TenantCard from "@/components/tenantCard";
import AcceptableUse from "@/components/Modal/AcceptanceTerms";
import RedirectingOverlay from "@/components/RedirectingOverlay";

export default {
  name: "dashboardPage",
  title: "Dashboard",
  components: {
    TenantCard,
    AcceptableUse,
    RedirectingOverlay,
    ExclamationIcon,
    SearchIcon
  },
  data() {
    return {
        itemComponent: TenantCard,
        tenants: [],
        noTenants: false,
        redirecting: false,
        selectedTenant: null,
        accepted: false,
        eulaVersion: 1.0,
        searchTerm: '',
    }
  },
  computed: {
    ...mapGetters(
        {
          getUser: "getUser",
          getLegalAcceptance: "getLegalAcceptance"
        }
    ),
    filteredList () {
      if (!this.searchTerm) {
        return this.tenants; // Return all items if no search term
      }
      const lowerCaseSearchTerm = this.searchTerm.toLowerCase();
      return this.tenants.filter(item =>
          item.tenant_name.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
  },
  methods: {
    ...mapMutations(["stampSessionHandoff"]),
    async checkEULA() {
      if(this.getUser.accepted_version < this.eulaVersion || this.getUser.accepted_agreement === false)
      {
        this.accepted = false;
      }
      else
      {
        this.accepted = true;
        await this.getEmployees();
      }
    },
    handleTenantSelect(tenant) {
      this.selectedTenant = tenant;
      this.redirecting = true;
      // Tell the tenant app this session is active as of now; its own idle clock lives
      // in that subdomain's localStorage and may be stale from a much earlier visit.
      this.stampSessionHandoff();
      location.href = tenant.tenant_url;
    },
    async getEmployees() {
      this.tenants = (await getEmployeesByUserId(this.getUser.id))
      if(this.tenants.length === 1)
      {
        this.handleTenantSelect(this.tenants[0]);
      }
      else if(!this.tenants.length >= 1){
        this.noTenants = true;
      }
    }
  },
  beforeMount() {
    this.checkEULA();
  },
  watch: {
    async getLegalAcceptance (val) {
      if (val === true) {
        await this.getEmployees();
      }
    }
  }
}
</script>

<style scoped>
.dash-page {
  font-family: 'Barlow', sans-serif;
  position: relative;
  z-index: 1;
}

/* ── Background (same as login page) ──────────────── */
.dash-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background-color: var(--cc-page);
  transition: background-color 0.3s;
}
.dash-bg__overlay {
  position: absolute;
  inset: 0;
  background: var(--cc-overlay-grad);
  transition: background 0.3s;
}

.dash-warn {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background: var(--cc-warn-bg);
  backdrop-filter: blur(28px) saturate(150%);
  -webkit-backdrop-filter: blur(28px) saturate(150%);
  border: 1px solid var(--cc-card-border);
  border-left: 3px solid var(--cc-warn-border);
  padding: 0.875rem 1rem;
  border-radius: 0 3px 3px 0;
  margin-bottom: 1.25rem;
  transition: background 0.3s, border-color 0.3s;
}
.dash-warn__icon { width: 18px; height: 18px; color: var(--cc-warn-title); flex-shrink: 0; margin-top: 2px; transition: color 0.3s; }
.dash-warn__title { font-size: 0.75rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--cc-warn-title); margin: 0 0 0.125rem; transition: color 0.3s; }
.dash-warn__body { font-size: 0.8125rem; color: var(--cc-warn-body); margin: 0; transition: color 0.3s; }

.dash-card {
  background: var(--cc-card);
  backdrop-filter: blur(28px) saturate(150%);
  -webkit-backdrop-filter: blur(28px) saturate(150%);
  border: 1px solid var(--cc-card-border);
  border-radius: 4px;
  overflow: hidden;
  box-shadow: var(--cc-card-shadow);
  transition: background 0.3s, border-color 0.3s, box-shadow 0.3s;
}

.dash-card__header {
  padding: 1.5rem 1.5rem 1.125rem;
  border-bottom: 1px solid var(--cc-sep-subtle);
  transition: border-color 0.3s;
}
.dash-card__title {
  font-family: 'Big Shoulders Display', sans-serif;
  font-size: 1.875rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  color: var(--cc-t1);
  margin: 0 0 0.25rem;
  transition: color 0.3s;
}
.dash-card__sub { font-size: 0.8125rem; color: var(--cc-t3); margin: 0; transition: color 0.3s; }

.dash-search-wrap {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--cc-sep-subtle);
  transition: border-color 0.3s;
}
.dash-search { position: relative; display: flex; align-items: center; }
.dash-search__icon { position: absolute; left: 0.75rem; width: 16px; height: 16px; color: var(--cc-t4); pointer-events: none; flex-shrink: 0; transition: color 0.3s; }
.dash-search__input {
  width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--cc-input-border);
  border-radius: 3px;
  padding: 0.625rem 0.875rem 0.625rem 2.5rem;
  font-family: 'Barlow', sans-serif;
  font-size: 0.9rem;
  color: var(--cc-input-text);
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.3s, color 0.3s;
  box-sizing: border-box;
}
.dash-search__input::placeholder { color: var(--cc-placeholder); }
.dash-search__input:focus { border-color: #0092bf; box-shadow: 0 0 0 3px rgba(0, 146, 191, 0.12); }

.dash-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  list-style: none;
  padding: 0;
  margin: 0;
}
@media (min-width: 640px) { .dash-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1024px) { .dash-grid { grid-template-columns: repeat(3, 1fr); } }

.dash-grid__item {
  border-bottom: 1px solid var(--cc-sep-subtle);
  border-right: 1px solid var(--cc-sep-subtle);
  transition: border-color 0.18s;
}
.dash-grid__item:hover { border-color: rgba(0, 146, 191, 0.18); }

</style>